<template>
  <div class="task-cards">
    <!-- Table Toolbar -->
    <table-toolbar
      :show-title="showTitle"
      :title="title"
      :show-count="true"
      :current-count="filteredTasks.length"
      :total-count="tasks.length"
      :searchable="searchable"
      :search-query="searchQuery"
      @search-change="searchQuery = $event"
      :show-view-toggle="true"
      :view-mode="viewMode"
      @view-mode-change="viewMode = $event"
      :show-column-visibility="showColumnVisibility"
      :columns="availableTableColumns"
      :visible-columns="visibleColumns"
      @column-toggle="handleColumnVisibilityChange"
    >
      <template #action-buttons>
        <themed-button
          variant="outline"
          size="default"
          @click="showFilters = !showFilters"
          :aria-label="showFilters ? 'Hide filters' : 'Show filters'"
        >
          <theme-icon icon="filter" type="icons" classes="w-4 h-4" />
        </themed-button>
        <themed-button
          v-if="canCreateTask"
          variant="default"
          size="default"
          @click="openCreateTaskModal"
          aria-label="Create new task"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>{{ createButtonText }}</span>
        </themed-button>
      </template>
    </table-toolbar>

    <!-- Filters Panel -->
    <task-filters
      v-if="showFilters"
      :value="activeFilters"
      :hidden-fields="projectId ? ['project'] : []"
      @input="activeFilters = $event"
    />

    <!-- Grid View -->
    <themed-grid
      v-if="viewMode === 'grid'"
      :data="filteredTasks"
      :fields="gridFields"
      :visible-fields="visibleGridFields"
      :columns="3"
      :pagination="false"
      :clickable="true"
      :loading="isLoading"
      empty-message="No tasks found"
      empty-icon="clipboard"
      item-key="id"
      @card-click="handleEditTask"
    />

    <!-- List View -->
    <themed-table
      v-else
      :data="filteredTasks"
      :columns="visibleTableColumns"
      :pagination="false"
      :searchable="false"
      :sortable="true"
      hoverable
      bordered
      :loading="isLoading"
      empty-message="No tasks found"
      empty-icon="clipboard"
      :column-visibility="false"
      @row-click="handleRowClick"
      aria-label="Tasks table"
    >
      <!-- Actions Column Slot -->
      <template #actions="{ row }">
        <themed-button
          variant="ghost"
          size="sm"
          @click.stop="handleEditTask(row)"
          aria-label="Edit task"
        >
          <theme-icon icon="edit" type="icons" classes="w-4 h-4" />
        </themed-button>
      </template>
    </themed-table>

    <!-- Task Form Modal -->
    <themed-modal
      v-if="showTaskModal"
      :name="modalName"
      :title="selectedTask ? 'Task Details' : 'Create Task'"
      size="lg"
      @close="closeTaskModal"
    >
      <!-- Detail view for existing tasks -->
      <task-detail
        v-if="selectedTask && selectedTask.id"
        ref="taskDetail"
        :task="selectedTask"
        @save="handleTaskSaved"
        @close="closeTaskModal"
        @open-task="handleOpenSubtask"
        @refresh="refreshSelectedTask"
        @loading-change="taskFormLoading = $event"
      />

      <!-- Create form for new tasks -->
      <task-form
        v-else
        ref="taskForm"
        :task="selectedTask"
        :project-id="projectId"
        :site-id="siteId"
        @save="handleTaskSaved"
        @cancel="closeTaskModal"
        @loading-change="taskFormLoading = $event"
        @loading-options-change="formLoading = $event"
      />
      
      <template #footer>
        <div class="modal-actions">
          <themed-button
            variant="secondary"
            @click="closeTaskModal"
            :disabled="taskFormLoading || formLoading"
          >
            Cancel
          </themed-button>
          <themed-button
            @click="submitTaskForm"
            :disabled="taskFormLoading || formLoading"
          >
            <theme-icon v-if="taskFormLoading || formLoading" icon="loading" type="icons" class="w-4 h-4 animate-spin" />
            <span v-else>{{ selectedTask && selectedTask.id ? 'Update Task' : 'Create Task' }}</span>
          </themed-button>
        </div>
      </template>
    </themed-modal>
  </div>
</template>

<script>
import ThemedTable from '../ThemedTable.vue';
import ThemedGrid from '../ThemedGrid.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableToolbar from '../table/TableToolbar.vue';
import ThemedModal from '../ThemedModal.vue';
import TaskForm from './TaskForm.vue';
import TaskDetail from './TaskDetail.vue';
import TaskFilters from './TaskFilters.vue';

export default {
  name: 'TaskCards',
  
  components: {
    ThemedTable,
    ThemedGrid,
    ThemedButton,
    ThemeIcon,
    TableToolbar,
    ThemedModal,
    TaskForm,
    TaskDetail,
    TaskFilters,
  },
  
  props: {
    projectId: {
      type: Number,
      default: null
    },
    siteId: {
      type: Number,
      default: null
    },
    title: {
      type: String,
      default: 'Tasks'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    createButtonText: {
      type: String,
      default: 'Create Task'
    },
    modalName: {
      type: String,
      default: 'task-cards-modal'
    },
    showColumnVisibility: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      tasks: [],
      isLoading: false,
      showTaskModal: false,
      taskFormLoading: false,
      formLoading: false,
      selectedTask: null,
      searchQuery: '',
      viewMode: 'list',
      visibleColumns: [],
      showFilters: false,
      activeFilters: {
        status_id: null,
        priority_id: null,
        assignee_type: null,
        assignee_id: null,
        project: null,
        site: null,
        due_date_from: null,
        due_date_to: null,
      },
    };
  },
  
  computed: {
    canCreateTask() {
      return this.$gate.can('create', 'task');
    },
    
    filteredTasks() {
      let tasks = this.tasks;

      // Search
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        tasks = tasks.filter(task =>
          task.title?.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          task.assignee?.name?.toLowerCase().includes(query) ||
          task.status?.name?.toLowerCase().includes(query) ||
          task.priority?.name?.toLowerCase().includes(query)
        );
      }

      // Status filter
      if (this.activeFilters.status_id) {
        tasks = tasks.filter(t => t.status_id == this.activeFilters.status_id);
      }

      // Priority filter
      if (this.activeFilters.priority_id) {
        tasks = tasks.filter(t => t.priority_id == this.activeFilters.priority_id);
      }

      // Assignee filter
      if (this.activeFilters.assignee_type && this.activeFilters.assignee_id) {
        tasks = tasks.filter(t =>
          t.assignee_type == this.activeFilters.assignee_type &&
          t.assignee_id == this.activeFilters.assignee_id
        );
      }

      // Due date range
      if (this.activeFilters.due_date_from) {
        tasks = tasks.filter(t => t.due_date && t.due_date >= this.activeFilters.due_date_from);
      }
      if (this.activeFilters.due_date_to) {
        tasks = tasks.filter(t => t.due_date && t.due_date <= this.activeFilters.due_date_to);
      }

      return tasks;
    },
    
    gridFields() {
      return [
        {
          field: 'title',
          type: 'title'
        },
        {
          field: 'assignee.name',
          label: 'Assignee',
          type: 'text',
          transform: (value) => value || 'Unassigned'
        },
        // Row 1: Start Date and Due Date
        {
          columns: 2,
          fields: [
            {
              field: 'start_date',
              label: 'Start Date',
              type: 'date'
            },
            {
              field: 'due_date',
              label: 'Due Date',
              type: 'date',
              classFunction: (item) => item.is_overdue ? 'text-destructive' : ''
            }
          ]
        },
        // Row 2: Status and Priority
        {
          columns: 2,
          fields: [
            {
              field: 'status.name',
              label: 'Status',
              type: 'text',
              badge: true,
              badgeClass: (value, item) => {
                const color = item.status?.color || 'gray';
                return `badge--${color}`;
              }
            },
            {
              field: 'priority.name',
              label: 'Priority',
              type: 'text',
              badge: true,
              badgeClass: (value, item) => {
                const color = item.priority?.color || 'gray';
                return `badge--${color}`;
              }
            }
          ]
        },
        // Row 3: Project and Site
        {
          columns: 2,
          fields: [
            {
              field: 'project.name',
              label: 'Project',
              type: 'text',
              transform: (value) => value || 'N/A'
            },
            {
              field: 'site.name',
              label: 'Site',
              type: 'text',
              transform: (value) => value || 'N/A'
            }
          ]
        },
        {
          field: 'description',
          label: 'Description',
          type: 'text',
          transform: (value) => value || 'N/A'
        }
      ];
    },
    
    tableColumns() {
      return [
        {
          field: 'task_key',
          label: 'ID',
          sortable: true,
          searchable: true,
          showInTable: true,
          width: '100px'
        },
        {
          field: 'title',
          label: 'Task',
          sortable: true,
          searchable: true,
          showInTable: true,
          alwaysVisible: true
        },
        {
          field: 'assignee.name',
          label: 'Assignee',
          sortable: true,
          searchable: true,
          showInTable: true,
          transform: (value) => value || 'Unassigned'
        },
        {
          field: 'status.name',
          label: 'Status',
          sortable: true,
          searchable: false,
          showInTable: true,
          badge: true,
          badgeClass: (value, row) => {
            const color = row.status?.color || 'gray';
            return `badge--${color}`;
          }
        },
        {
          field: 'priority.name',
          label: 'Priority',
          sortable: true,
          searchable: false,
          showInTable: true,
          badge: true,
          badgeClass: (value, row) => {
            const color = row.priority?.color || 'gray';
            return `badge--${color}`;
          }
        },
        {
          field: 'subtask_count',
          label: 'Sub-tasks',
          sortable: true,
          searchable: false,
          showInTable: false,
          transform: (value) => value || 0
        },
        {
          field: 'comment_count',
          label: 'Comments',
          sortable: true,
          searchable: false,
          showInTable: false,
          transform: (value) => value || 0
        },
        {
          field: 'start_date',
          label: 'Start Date',
          sortable: true,
          searchable: false,
          showInTable: true,
          type: 'date'
        },
        {
          field: 'due_date',
          label: 'Due Date',
          sortable: true,
          searchable: false,
          showInTable: true,
          type: 'date',
          classFunction: (row) => row.is_overdue ? 'text-destructive' : ''
        },
        {
          field: 'project.name',
          label: 'Project',
          sortable: true,
          searchable: true,
          showInTable: true,
          transform: (value) => value || 'N/A'
        },
        {
          field: 'site.name',
          label: 'Site',
          sortable: true,
          searchable: true,
          showInTable: false,
          transform: (value) => value || 'N/A'
        },
        {
          field: 'description',
          label: 'Description',
          sortable: false,
          searchable: true,
          showInTable: false
        },
        {
          field: 'notes',
          label: 'Notes',
          sortable: false,
          searchable: true,
          showInTable: false
        },
        {
          field: 'creator.name',
          label: 'Created By',
          sortable: true,
          searchable: true,
          showInTable: true,
          transform: (value) => value || 'Unknown'
        },
        {
          field: 'actions',
          label: '',
          sortable: false,
          searchable: false,
          type: 'actions',
          width: '80px',
          alwaysVisible: true
        }
      ];
    },
    
    availableTableColumns() {
      // Return ALL columns except actions for the column visibility dropdown
      // Users should be able to toggle any column on/off
      return this.tableColumns.filter(col => {
        // Exclude action columns from the visibility toggle
        return col.type !== 'actions';
      });
    },
    
    visibleTableColumns() {
      // Filter columns based on visibleColumns array
      const filtered = this.tableColumns.filter(col => {
        // Always show columns marked as alwaysVisible
        if (col.alwaysVisible) {
          return true;
        }
        // Show if in visibleColumns array
        return this.visibleColumns.includes(col.field);
      });
      
      return filtered;
    },
    
    visibleGridFields() {
      // Map table column fields to grid field names
      // Grid fields use the same field names as table columns
      return this.visibleColumns;
    }
  },
  
  mounted() {
    this.fetchTasks();
    this.loadViewMode();
    this.loadVisibleColumns();
  },
  
  watch: {
    viewMode(newValue) {
      // Save view mode preference
      localStorage.setItem('tasks_view_mode', newValue);
    },
    
    projectId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchTasks();
      }
    },
    
    siteId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchTasks();
      }
    }
  },
  
  methods: {
    loadViewMode() {
      const savedViewMode = localStorage.getItem('tasks_view_mode');
      if (savedViewMode && ['grid', 'list'].includes(savedViewMode)) {
        this.viewMode = savedViewMode;
      }
    },
    
    async fetchTasks() {
      this.isLoading = true;
      
      try {
        const params = {
          per_page: 100
        };
        
        if (this.projectId) {
          params.project = this.projectId;
        }
        
        if (this.siteId) {
          params.site = this.siteId;
        }
        
        const response = await axios.get('/api/tasks', { params });
        this.tasks = response.data.data || response.data;
      } catch (error) {
        console.error('Error fetching tasks:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: error.response?.data?.message || 'Failed to load tasks'
        });
      } finally {
        this.isLoading = false;
      }
    },
    
    openCreateTaskModal() {
      this.selectedTask = null;
      this.showTaskModal = true;
      this.$nextTick(() => {
        this.$modal.show(this.modalName);
      });
    },
    
    closeTaskModal() {
      this.$modal.hide(this.modalName);
      this.showTaskModal = false;
      this.selectedTask = null;
      this.taskFormLoading = false;
      this.formLoading = false;
    },
    
    handleEditTask(task) {
      this.selectedTask = task;
      this.showTaskModal = true;
      this.$nextTick(() => {
        this.$modal.show(this.modalName);
      });
    },
    
    async handleTaskSaved(task) {
      // Refresh tasks list
      await this.fetchTasks();
      
      // Close modal
      this.closeTaskModal();
      
      // Emit event for parent components
      this.$emit('task-saved', task);
    },
    
    submitTaskForm() {
      if (this.$refs.taskDetail) {
        this.$refs.taskDetail.submitForm();
      } else if (this.$refs.taskForm) {
        this.$refs.taskForm.handleSubmit();
      }
    },

    handleOpenSubtask(subtask) {
      // Add a brief loading state to prevent jarring transition
      this.formLoading = true;
      this.selectedTask = null;
      
      // Use nextTick to ensure the form is fully unmounted before loading new task
      this.$nextTick(() => {
        this.selectedTask = subtask;
        // formLoading will be set to false by TaskForm when it finishes loading
      });
    },

    async refreshSelectedTask() {
      if (!this.selectedTask || !this.selectedTask.id) return;
      try {
        const response = await axios.get(`/api/tasks/${this.selectedTask.id}`);
        this.selectedTask = response.data;
      } catch (error) {
        console.error('Error refreshing task:', error);
      }
    },
    
    handleRowClick(task) {
      // ThemedTable emits { row } — extract the actual task object
      this.handleEditTask(task.row || task);
    },
    
    formatDate(date) {
      if (!date) return 'N/A';
      
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    loadVisibleColumns() {
      // Load saved column visibility preferences from localStorage
      const savedColumns = localStorage.getItem('tasks_table_visible_columns');
      
      if (savedColumns) {
        try {
          const parsed = JSON.parse(savedColumns);
          if (Array.isArray(parsed)) {
            this.visibleColumns = parsed;
          } else {
            console.warn('Invalid column preferences format, resetting to defaults');
            localStorage.removeItem('tasks_table_visible_columns');
            this.visibleColumns = this.getDefaultVisibleColumns();
          }
        } catch (e) {
          console.error('Failed to parse saved column preferences:', e);
          localStorage.removeItem('tasks_table_visible_columns');
          this.visibleColumns = this.getDefaultVisibleColumns();
        }
      } else {
        // No saved preferences, use defaults
        this.visibleColumns = this.getDefaultVisibleColumns();
      }
    },
    
    getDefaultVisibleColumns() {
      // Return default visible columns (those with showInTable: true)
      return this.tableColumns
        .filter(col => col.showInTable === true)
        .map(col => col.field);
    },
    
    handleColumnVisibilityChange({ field, visible }) {
      // Update visible columns array
      if (visible) {
        // Add field if not already in array
        if (!this.visibleColumns.includes(field)) {
          this.visibleColumns.push(field);
        }
      } else {
        // Remove field from array using splice for Vue reactivity
        const index = this.visibleColumns.indexOf(field);
        if (index > -1) {
          this.visibleColumns.splice(index, 1);
        }
      }
      
      // Save to localStorage
      localStorage.setItem('tasks_table_visible_columns', JSON.stringify(this.visibleColumns));
    }
  }
};
</script>
